import { AutoAssignType } from '../api/bucket-types';
import { useBudgetId, useBudgetSummary } from '../../budget/budget-store';
import { useMemo } from 'react';
import { calculateTargetAssignment } from '../../assignment/auto-assignment-target';
import { useBuckets } from '../api/use-get-buckets';
import { DateTime } from 'luxon';
import { calculateMonthlyRecurrences } from '../../recurring-transaction/recurring-transaction-utils';
import { useAllFutureTransactions } from '../../transaction/transaction-utils';
import { toDollars, toPennies } from '../../utils/format';
/**
 * Enum representing different types of bucket underfunding
 */
export var UnderfundedType;
(function (UnderfundedType) {
    UnderfundedType["OVERDRAFT"] = "OVERDRAFT";
    UnderfundedType["AUTO_ASSIGN_FIXED"] = "AUTO_ASSIGN_FIXED";
    UnderfundedType["AUTO_ASSIGN_FUTURE"] = "AUTO_ASSIGN_FUTURE";
    UnderfundedType["UPCOMING_TRANSACTION"] = "UPCOMING_TRANSACTION";
})(UnderfundedType || (UnderfundedType = {}));
/**
 * Hook that checks multiple buckets for underfunding
 *
 * @param yearMonth Year and month in YYYY-MM format
 * @param bucketIds Optional set of specific bucket IDs to check (if not provided, checks all buckets)
 * @returns Map of bucket IDs to their underfunded results
 */
export function useBucketsUnderfunded(yearMonth, bucketIds) {
    const buckets = useBuckets();
    const budgetSummary = useBudgetSummary();
    // Parse the year and month once
    const [yearStr, monthStr] = yearMonth.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    // Use the new utility to get pre-filtered future transactions
    const futureTransactions = useAllFutureTransactions(year, month);
    return useMemo(() => {
        const bucketIdSet = bucketIds ? new Set(bucketIds) : undefined;
        return checkBucketsUnderfunded(buckets, yearMonth, year, month, budgetSummary, futureTransactions, bucketIdSet);
    }, [buckets, bucketIds, yearMonth, year, month, budgetSummary, futureTransactions]);
}
/**
 * A utility to check multiple buckets for underfunding in a more efficient way
 * that only processes relevant future transactions
 *
 * @param buckets - List of buckets to check
 * @param yearMonth - Year and month in YYYY-MM format
 * @param year - Year as a number
 * @param month - Month as a number (1-12)
 * @param budgetSummary - Budget summary data
 * @param futureTransactions - Pre-filtered list of future transactions for the month
 * @param bucketIds - Optional set of bucket IDs to check
 * @returns Map of bucket IDs to their underfunded results
 */
export function checkBucketsUnderfunded(buckets, yearMonth, year, month, budgetSummary, futureTransactions, bucketIds) {
    const results = new Map();
    // Skip work if no buckets to check
    if (!buckets.length) {
        return results;
    }
    // Pre-compute upcoming outflows by bucketId
    const upcomingOutflowsByBucket = new Map();
    // Initialize outflows for each bucket we're checking
    buckets.forEach((bucket) => {
        if (!bucketIds || bucketIds.has(bucket.id)) {
            upcomingOutflowsByBucket.set(bucket.id, 0);
        }
    });
    // Process pre-filtered future transactions
    futureTransactions.forEach((transaction) => {
        // Skip transactions without a bucket
        if (!transaction.bucketId) {
            return;
        }
        // Skip if we're only checking specific buckets and this isn't one of them
        if (bucketIds && !bucketIds.has(transaction.bucketId)) {
            return;
        }
        // Add outflow to the total for this bucket (we already know it's a future transaction)
        const currentOutflow = upcomingOutflowsByBucket.get(transaction.bucketId) || 0;
        const transactionAmount = (transaction.outflow || 0) - (transaction.inflow || 0);
        upcomingOutflowsByBucket.set(transaction.bucketId, currentOutflow + transactionAmount);
    });
    // Now check each bucket with our pre-computed outflow data
    buckets.forEach((bucket) => {
        // Skip if bucket is not in specified set (if any)
        if (bucketIds && !bucketIds.has(bucket.id)) {
            return;
        }
        const bucketSummaryForMonth = budgetSummary.summaryByMonth.get(yearMonth)?.bucketSummaryById.get(bucket.id);
        if (!bucketSummaryForMonth) {
            results.set(bucket.id, null);
            return;
        }
        // Check for overdraft condition
        const overdraftResult = checkForOverdraft(bucketSummaryForMonth.available);
        if (overdraftResult) {
            results.set(bucket.id, overdraftResult);
            return;
        }
        // Check for auto-assign fixed condition
        const autoAssignFixedResult = checkForAutoAssignFixed(bucketSummaryForMonth.assigned, bucketSummaryForMonth.available, bucket.autoAssign);
        if (autoAssignFixedResult) {
            results.set(bucket.id, autoAssignFixedResult);
            return;
        }
        // Check for auto-assign future/target condition
        const autoAssignFutureResult = checkForAutoAssignFuture(bucketSummaryForMonth, bucket.autoAssign, year, month);
        if (autoAssignFutureResult) {
            results.set(bucket.id, autoAssignFutureResult);
            return;
        }
        // Check for upcoming transactions using our precomputed outflows
        const totalUpcomingOutflow = upcomingOutflowsByBucket.get(bucket.id) || 0;
        // Only record underfunding if there are upcoming outflows that exceed available funds
        if (totalUpcomingOutflow > 0 && totalUpcomingOutflow > bucketSummaryForMonth.available) {
            results.set(bucket.id, {
                type: UnderfundedType.UPCOMING_TRANSACTION,
                amount: totalUpcomingOutflow - bucketSummaryForMonth.available,
            });
            return;
        }
        // No underfunding detected
        results.set(bucket.id, null);
    });
    return results;
}
/**
 * Checks if a bucket has an overdraft
 * @param available The available amount in the bucket
 * @returns Underfunded result or null if no overdraft
 */
export function checkForOverdraft(available) {
    if (available < 0) {
        return {
            type: UnderfundedType.OVERDRAFT,
            amount: Math.abs(available),
        };
    }
    return null;
}
/**
 * Checks if a bucket has fixed auto-assign underfunding
 * @param assigned The assigned amount in the bucket
 * @param available The available amount in the bucket
 * @param autoAssign The auto-assign configuration
 * @returns Underfunded result or null if no auto-assign underfunding
 */
export function checkForAutoAssignFixed(assigned, available, autoAssign) {
    // early returns for conditions that would make auto-assign not applicable
    if (!autoAssign) {
        return null;
    }
    if (autoAssign.disabled) {
        return null;
    }
    if (autoAssign.type !== AutoAssignType.FIXED) {
        return null;
    }
    if (!autoAssign.amount || autoAssign.amount <= 0) {
        return null;
    }
    // manual assignments override the auto assignment
    if (assigned !== 0) {
        return null;
    }
    // without the top off we assign the full amount regardless of available
    if (!autoAssign.topOff) {
        return {
            type: UnderfundedType.AUTO_ASSIGN_FIXED,
            amount: autoAssign.amount,
        };
    }
    // with top off we only fill up the difference between the target and available amount
    const neededAmount = toDollars(Math.max(0, toPennies(autoAssign.amount) - toPennies(available)));
    if (neededAmount > 0) {
        return {
            type: UnderfundedType.AUTO_ASSIGN_FIXED,
            amount: neededAmount,
        };
    }
    // No underfunding found
    return null;
}
/**
 * Checks if a bucket has future target auto-assign underfunding
 * @param bucketSummary The summary for the bucket
 * @param autoAssign The auto-assign configuration
 * @param currentYear The current year
 * @param currentMonth The current month (1-12)
 * @returns Underfunded result or null if no future target underfunding
 */
export function checkForAutoAssignFuture(bucketSummary, autoAssign, currentYear, currentMonth) {
    // Early returns for conditions that would make auto-assign target not applicable
    if (!autoAssign) {
        return null;
    }
    if (autoAssign.disabled) {
        return null;
    }
    if (autoAssign.type !== AutoAssignType.TARGET) {
        return null;
    }
    if (!autoAssign.amount || autoAssign.amount <= 0) {
        return null;
    }
    if (!autoAssign.target) {
        return null;
    }
    // Calculate the target assignment
    const result = calculateTargetAssignment({
        targetAmount: autoAssign.amount,
        targetDate: autoAssign.target,
        currentMonth,
        currentYear,
        assigned: bucketSummary.assigned,
        leftover: bucketSummary.leftover,
        outflow: bucketSummary.outflow,
        available: bucketSummary.available,
        recurrenceMonths: autoAssign.recurrenceMonths ?? 0,
    });
    // Only return a result if there's a shortfall
    if (result.shortfall > 0) {
        return {
            type: UnderfundedType.AUTO_ASSIGN_FUTURE,
            amount: result.shortfall,
        };
    }
    return null;
}
/**
 * Checks if a bucket has upcoming transactions that exceed the available budget
 * @param bucketId The ID of the bucket
 * @param available The available amount in the bucket
 * @param transactions All transactions
 * @param recurringTransactions All recurring transactions
 * @param yearMonth Year and month in YYYY-MM format
 * @param timezone User's timezone
 * @returns Underfunded result or null if no upcoming transaction underfunding
 */
export function checkForUpcomingTransactions(bucketId, available, transactions, recurringTransactions, yearMonth, timezone) {
    // Get the current date in the user's timezone
    const now = DateTime.now().setZone(timezone).startOf('day');
    // Parse the year and month
    const [year, month] = yearMonth.split('-').map(Number);
    // Create start and end of month DateTime objects
    const startOfMonth = DateTime.fromObject({ year, month, day: 1 }, { zone: timezone });
    const endOfMonth = startOfMonth.endOf('month');
    // Find upcoming regular transactions in this month for this bucket
    const upcomingTransactions = transactions.filter((transaction) => {
        // Only include transactions for this bucket
        if (transaction.bucketId !== bucketId) {
            return false;
        }
        // Convert transaction date to DateTime in user's timezone
        const transactionDate = DateTime.fromSeconds(transaction.dateEpoch).setZone(timezone).startOf('day');
        // Check if transaction is in the future but within this month
        return transactionDate > now && transactionDate >= startOfMonth && transactionDate <= endOfMonth;
    });
    // Calculate upcoming recurring transactions for this month and bucket
    const recurringInstances = calculateMonthlyRecurrences(recurringTransactions, year, month, timezone).instances.filter((instance) => instance.bucketId === bucketId && DateTime.fromSeconds(instance.dateEpoch).setZone(timezone).startOf('day') > now);
    // If there are no upcoming transactions of either type, return null
    if (upcomingTransactions.length === 0 && recurringInstances.length === 0) {
        return null;
    }
    // Calculate the total outflow from upcoming regular transactions
    const totalRegularOutflow = upcomingTransactions.reduce((sum, transaction) => {
        return sum + (transaction.outflow || 0) - (transaction.inflow || 0);
    }, 0);
    // Calculate the total outflow from upcoming recurring transactions
    const totalRecurringOutflow = recurringInstances.reduce((sum, transaction) => {
        return sum + (transaction.outflow || 0) - (transaction.inflow || 0);
    }, 0);
    // Sum both types of upcoming outflows
    const totalUpcomingOutflow = totalRegularOutflow + totalRecurringOutflow;
    // If total outflow exceeds available funds, the bucket is underfunded
    if (totalUpcomingOutflow > available) {
        return {
            type: UnderfundedType.UPCOMING_TRANSACTION, // Using same type for both sources
            amount: totalUpcomingOutflow - available,
        };
    }
    return null;
}
/**
 * Hook to calculate total underfunded amount and create assignment requests
 *
 * @param year - The target year for assignment
 * @param month - The target month for assignment
 * @param bucketsUnderfunded - Map of bucket IDs to their underfunded results
 * @param bucketIds - Optional set of bucket IDs to filter underfunded results
 * @returns Object containing total underfunded amount and complete assignment request
 */
export function useAutoAssignmentRequest(year, month, bucketsUnderfunded, bucketIds) {
    const budgetId = useBudgetId();
    const budgetSummary = useBudgetSummary();
    const yearMonth = `${year}-${month.toString().padStart(2, '0')}`;
    const monthSummary = budgetSummary.summaryByMonth.get(yearMonth);
    // Calculate the total underfunded amount and create assignment requests
    return useMemo(() => {
        let totalAmount = 0;
        const requests = {};
        bucketsUnderfunded.forEach((result, bucketId) => {
            // Skip if bucket is not in specified set (if provided and not empty)
            if (bucketIds?.size && !bucketIds.has(bucketId)) {
                return;
            }
            if (!result) {
                return;
            }
            totalAmount += result.amount;
            const currentAssignment = monthSummary?.bucketSummaryById.get(bucketId)?.assigned ?? 0;
            requests[bucketId] = {
                year,
                month,
                amount: currentAssignment + result.amount,
            };
        });
        // Return both the total amount and the complete assignment request
        return {
            totalAmount,
            assignmentRequest: {
                budgetId,
                requests,
            },
        };
    }, [bucketsUnderfunded, year, month, yearMonth, budgetSummary, bucketIds, budgetId]);
}
